import { forwardRef } from 'react';
import type { CurrencyCode } from 'v5/core/money/model';
import { parseMoneyInput } from 'v5/core/money/moneyInput';
import { Input, InputProps } from 'v5/design-system/Input';
import {
  InputGroupInput,
  InputGroupInputProps
} from 'v5/design-system/InputGroup';
import {
  draftNumberOf,
  useDraftNumber
} from 'v5/patterns/forms/fields/money/useDraftNumber';

type NumberFieldProps = {
  value: number | null | undefined;
  onChange(value: number | null): void;
  onBlur(): void;
  /** Committed value → text; `String` unless the field shows money. */
  format?(value: number): string;
  /** A money amount: its own currency's code or symbol may be typed with it. */
  currency?: CurrencyCode;
};

/**
 * A `number | null` form value typed as text (legacy `NumberInput` kept the
 * typed string too): `100.0` stays while typing, `12.` saves 12, unreadable
 * text saves `NaN` for the codec to refuse. A text input, not
 * `type="number"`, whose value reads empty for `12.` or `-`.
 */
function useNumberText({
  value,
  onChange,
  onBlur,
  format = String,
  currency
}: NumberFieldProps) {
  return useDraftNumber({
    value: value ?? null,
    onChange,
    onBlur,
    format,
    parse: text =>
      draftNumberOf(parseMoneyInput(text, { decimalSeparator: '.', currency }))
  });
}

/** `Input` over a `number | null` form field (see `useNumberText`). */
export const NumberInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'type'> & NumberFieldProps
>(function NumberInput(
  { value, onChange, onBlur, format, currency, ...props },
  ref
) {
  const draft = useNumberText({ value, onChange, onBlur, format, currency });
  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      {...props}
      {...draft}
    />
  );
});

/** `InputGroupInput` over a `number | null` form field (see `useNumberText`). */
export const NumberGroupInput = forwardRef<
  HTMLInputElement,
  Omit<InputGroupInputProps, 'value' | 'onChange' | 'onBlur' | 'type'> &
    NumberFieldProps
>(function NumberGroupInput(
  { value, onChange, onBlur, format, currency, ...props },
  ref
) {
  const draft = useNumberText({ value, onChange, onBlur, format, currency });
  return (
    <InputGroupInput
      ref={ref}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      {...props}
      {...draft}
    />
  );
});
